import { Component } from '@theme/component';
import { fetchConfig, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

const CART_UPDATE_RATE_LIMIT_MESSAGE = 'Cart is updating too quickly. Please wait a moment, then try again.';
const CART_UPDATE_ERROR_MESSAGE = 'Cart could not update. Please try again.';
const CART_SERVICE_UNAVAILABLE_MESSAGE = 'Cart is catching up. Please wait a moment, then try again.';

class CartRequestError extends Error {
  /**
   * @param {string} message
   * @param {{ status?: number }} [options]
   */
  constructor(message, options = {}) {
    super(message);
    this.name = 'CartRequestError';
    this.status = options.status;
  }
}

/**
 * @param {unknown} error
 * @returns {string}
 */
function formatCartError(error) {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (Array.isArray(error)) return error.map(formatCartError).filter(Boolean).join(' ');
  if (typeof error === 'object') return Object.values(error).map(formatCartError).filter(Boolean).join(' ');

  return String(error);
}

/**
 * @param {unknown} payload
 * @param {number} [status]
 * @returns {string}
 */
function getCartErrorMessage(payload, status) {
  if (payload && typeof payload === 'object') {
    const data = /** @type {{ errors?: unknown; description?: unknown; message?: unknown; status?: unknown }} */ (payload);
    const responseStatus = typeof data.status === 'number' ? data.status : status;

    if (responseStatus === 429) return CART_UPDATE_RATE_LIMIT_MESSAGE;
    if (responseStatus === 503) return CART_SERVICE_UNAVAILABLE_MESSAGE;

    const serverMessage =
      formatCartError(data.errors) || formatCartError(data.description) || formatCartError(data.message);

    if (serverMessage.includes('SERVICE_UNAVAILABLE')) return CART_SERVICE_UNAVAILABLE_MESSAGE;

    return serverMessage || CART_UPDATE_ERROR_MESSAGE;
  }

  if (status === 429) return CART_UPDATE_RATE_LIMIT_MESSAGE;
  if (status === 503) return CART_SERVICE_UNAVAILABLE_MESSAGE;

  return CART_UPDATE_ERROR_MESSAGE;
}

/**
 * @param {Response} response
 * @returns {Promise<Object>}
 */
async function parseCartResponse(response) {
  const responseText = await response.text();
  let data = {};

  if (responseText) {
    try {
      data = JSON.parse(responseText);
    } catch (_error) {
      throw new CartRequestError(getCartErrorMessage(null, response.status), { status: response.status });
    }
  }

  if (!response.ok) {
    throw new CartRequestError(getCartErrorMessage(data, response.status), { status: response.status });
  }

  return data;
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #onQuantityChangeHandler = this.#onQuantityChange.bind(this);

  /** @type {{ line: number; quantity: number; action: string } | null} */
  #queuedQuantityUpdate = null;

  /** @type {boolean} */
  #isProcessingQuantityUpdate = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChangeHandler);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChangeHandler);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    const textComponent = /** @type {TextComponent | null | undefined} */ (
      cartItemRowToRemove?.querySelector('text-component')
    );
    textComponent?.shimmer();
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    this.#queuedQuantityUpdate = config;

    if (this.#isProcessingQuantityUpdate) return;

    this.#processQuantityUpdateQueue();
  }

  async #processQuantityUpdateQueue() {
    this.#isProcessingQuantityUpdate = true;

    try {
      while (this.#queuedQuantityUpdate) {
        const config = this.#queuedQuantityUpdate;
        this.#queuedQuantityUpdate = null;

        const didUpdate = await this.#performQuantityUpdate(config);
        if (!didUpdate) {
          this.#queuedQuantityUpdate = null;
        }
      }
    } finally {
      this.#isProcessingQuantityUpdate = false;
    }
  }

  /**
   * @param {{ line: number; quantity: number; action: string }} config
   * @returns {Promise<boolean>}
   */
  async #performQuantityUpdate(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    let { line, quantity } = config;
    const { cartTotal } = this.refs;
    this.#clearCartError(line);

    const quantitySelector = this.refs.quantitySelectors?.[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');
    if (window.bzInventoryGuard?.clampCartQuantity) {
      try {
        const guardResult = await window.bzInventoryGuard.clampCartQuantity({
          line,
          quantity,
          variantId: quantitySelector?.dataset.variantId,
          handle: quantitySelector?.dataset.productHandle,
          sourceElement: quantityInput || quantitySelector,
        });
        quantity = guardResult.quantity;
        if (quantityInput instanceof HTMLInputElement && quantityInput.value !== String(quantity)) {
          quantityInput.value = String(quantity);
          quantitySelector?.updateButtonStates?.();
        }
      } catch (error) {
        this.#handleCartRequestError(line, error);
        return false;
      }
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    try {
      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      const parsedResponseText = await parseCartResponse(response);

      if (parsedResponseText.errors || parsedResponseText.status) {
        this.#handleCartError(line, parsedResponseText);
        return false;
      }

      const sectionHTML = parsedResponseText.sections?.[this.sectionId];
      const newSectionHTML =
        typeof sectionHTML === 'string' ? new DOMParser().parseFromString(sectionHTML, 'text/html') : null;

      // Grab the new cart item count from a hidden element
      const newCartHiddenItemCount = newSectionHTML?.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount
        ? parseInt(newCartHiddenItemCount, 10)
        : Number(parsedResponseText.item_count) || 0;

      // Update data-cart-quantity for all matching variants
      this.#updateQuantitySelectors(parsedResponseText);

      this.dispatchEvent(
        new CartUpdateEvent(parsedResponseText, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponseText.sections,
        })
      );

      try {
        if (typeof sectionHTML === 'string') {
          await morphSection(this.sectionId, sectionHTML, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          await sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      } catch (renderError) {
        console.warn('Cart updated, but the cart section could not be refreshed.', renderError);
      }

      this.#updateCartQuantitySelectorButtonStates();
      return true;
    } catch (error) {
      this.#handleCartRequestError(line, error);
      return false;
    } finally {
      resetShimmer(this);
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const errorMessage = getCartErrorMessage(parsedResponseText);
    const quantitySelector = this.refs.quantitySelectors?.[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = quantityInput.defaultValue;
      quantitySelector.updateButtonStates?.();
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement) || !(cartItemErrorContainer instanceof HTMLElement)) {
      console.warn(errorMessage);
      return;
    }

    cartItemError.textContent = errorMessage;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * @param {number} line
   */
  #clearCartError(line) {
    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (cartItemError instanceof HTMLElement) {
      cartItemError.textContent = '';
    }

    if (cartItemErrorContainer instanceof HTMLElement) {
      cartItemErrorContainer.classList.add('hidden');
    }
  }

  /**
   * @param {number} line
   * @param {unknown} error
   */
  #handleCartRequestError(line, error) {
    const message = error instanceof CartRequestError ? error.message : CART_UPDATE_ERROR_MESSAGE;

    if (error instanceof CartRequestError) {
      console.warn(message);
    } else {
      console.error(error);
    }

    this.#handleCartError(line, { errors: message });
  }

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer
        .renderSection(this.sectionId, { cache: false, mode: this.isDrawer ? 'hydration' : 'full' })
        .catch((error) => console.warn('Cart discount applied, but the cart section could not refresh.', error));
      return;
    }
    if (event.target === this) return;
    if (this.isDrawer && this.closest('cart-drawer-component')?.hasAttribute('data-cart-syncing')) return;

    const cartItemsHtml = event.detail?.data?.sections?.[this.sectionId];
    const mode = this.isDrawer ? 'hydration' : 'full';
    if (!cartItemsHtml) {
      // A closed drawer is redrawn when it opens (cart-drawer.js open() -> #syncCart), so rendering
      // the ~118 KB header now only costs the shopper time. The /cart page still refreshes here.
      const drawerDialog = this.isDrawer ? this.closest('dialog') : null;
      if (drawerDialog && !drawerDialog.open) return;
      this.#queueSectionRefresh(mode);
      return;
    }

    Promise.resolve(morphSection(this.sectionId, cartItemsHtml, { mode }))
      .then(() => {
        // Update button states for all cart quantity selectors after morph.
        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => console.warn('Cart changed, but the cart section could not refresh.', error));
  };

  /**
   * 2026-10-25 speed (Big Z's): one server render of this section per burst of cart events.
   * One add fires several cart:update events within a second or two (the add itself, the
   * inventory guard, ride-along, the ship-date panel), and each used to fetch and morph the whole
   * header section (~118 KB of HTML). Now the first event waits a beat for the rest, one render
   * runs, and anything that lands while it runs gets exactly one more render afterwards.
   */
  #refreshTimer = 0;
  #refreshRunning = null;
  #refreshAgain = false;

  /** @param {string} mode */
  #queueSectionRefresh(mode) {
    if (this.#refreshRunning) {
      this.#refreshAgain = true;
      return;
    }
    clearTimeout(this.#refreshTimer);
    this.#refreshTimer = setTimeout(() => {
      this.#refreshTimer = 0;
      this.#refreshRunning = Promise.resolve(sectionRenderer.renderSection(this.sectionId, { cache: false, mode }))
        .then(() => this.#updateCartQuantitySelectorButtonStates())
        .catch((error) => console.warn('Cart changed, but the cart section could not refresh.', error))
        .finally(() => {
          this.#refreshRunning = null;
          if (this.#refreshAgain) {
            this.#refreshAgain = false;
            this.#queueSectionRefresh(mode);
          }
        });
    }, 150);
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
    this.setAttribute('aria-busy', 'true');

    for (const control of this.querySelectorAll('button, input, select, textarea')) {
      if (
        !(
          control instanceof HTMLButtonElement ||
          control instanceof HTMLInputElement ||
          control instanceof HTMLSelectElement ||
          control instanceof HTMLTextAreaElement
        )
      ) {
        continue;
      }

      if (control.disabled) {
        control.setAttribute('data-cart-items-was-disabled', 'true');
      } else {
        control.setAttribute('data-cart-items-temporarily-disabled', 'true');
        control.disabled = true;
      }
    }
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
    this.removeAttribute('aria-busy');

    for (const control of this.querySelectorAll('button, input, select, textarea')) {
      if (
        !(
          control instanceof HTMLButtonElement ||
          control instanceof HTMLInputElement ||
          control instanceof HTMLSelectElement ||
          control instanceof HTMLTextAreaElement
        )
      ) {
        continue;
      }

      if (control.hasAttribute('data-cart-items-temporarily-disabled')) {
        control.disabled = false;
        control.removeAttribute('data-cart-items-temporarily-disabled');
      }

      control.removeAttribute('data-cart-items-was-disabled');
    }

    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
